// One task in full (#1034) — `GET /v1/tasks/:id` (`projectTaskDetail`), the read
// half of the two `/v1/tasks` endpoints the queue board doesn't cover.
// `lib/task-dispatch.ts` holds the write half (`POST /v1/tasks`), `lib/fleet.ts`
// the list half — and `TaskRow` is imported from there, because detail is that
// row EXTENDED, never a second shape.

import type { TaskFailureKind, TaskOrigin, TaskRunState, TaskSink } from '@developerz/domain';
import { type QueryKey, useQueryClient } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { TaskRow } from './fleet';
import type { LiveInterest } from './live/entities';
import { createLiveQuery } from './live/live-query';
import { heldBy, type MoveRead } from './move-read';
import { orderStamp } from './read-order';
import { SESSION_TOKEN } from './session';

/** The run's 1-row pulse — the view the control-MCP `run_status` verb also serves. */
export type TaskRunPulse = {
  readonly run_id: string;
  readonly last_seq: number | null;
  readonly last_kind: string | null;
  readonly last_at: string | null;
  /** `state === 'finished'` only. `false` is NOT live: render `state` (#5918). */
  readonly finished: boolean;
  /** Where the run is — decided server-side, the one field the pill renders. */
  readonly state: TaskRunState;
};

/**
 * One rung of the per-task attempt ladder (#2760). One row per `run_id` the
 * task has accumulated — every retry opens a fresh `run_id`, so a task with
 * three attempts reads as three rows. Ordered by attempt (the seq of each
 * attempt's first event), so attempt 1 reads first.
 *
 * `outcome` is the terminal disposition of THIS attempt — distinct from
 * `tasks.status`, which is the durable task-level state and overwrites on
 * retry. The ladder records each attempt's terminal event so a task with
 * three burnt attempts reads as three rows.
 *
 * `failure_kind` is RE-DERIVED from the terminal `error` payload via
 * `classifyTaskFailure` — the same domain predicate `tasks.failure_kind` is
 * written from. The ladder's vocabulary matches the DTO's by construction.
 */
export type TaskAttempt = {
  readonly run_id: string;
  /** Seq of the first event of this attempt; orders attempts 1 → N. */
  readonly seq: number;
  /** Terminal event timestamp. */
  readonly ts: string;
  readonly outcome: 'success' | 'failure' | 'pending';
  /** Re-derived {@link TaskFailureKind}; null when the attempt did not fail. */
  readonly failure_kind: TaskFailureKind | null;
  /** Terminal `error` payload verbatim; null when the attempt did not fail. */
  readonly failure_payload: unknown;
};

/**
 * `GET /v1/tasks/:id` — the queue row PLUS the scoped-plan detail the list
 * summarizes away and the run pulse. `queue_position` / `depth` stay null here:
 * the queue standing is an account-wide list concern, never a single-row one.
 */
export type TaskDetail = TaskRow & {
  readonly priority: number;
  readonly origin: TaskOrigin;
  readonly sink: TaskSink;
  readonly confidence: number;
  readonly problem: string;
  readonly acceptance_criteria: readonly string[];
  readonly files_touched: readonly string[];
  readonly forbidden: readonly string[];
  readonly depends_on: readonly string[];
  readonly updated_at: string;
  /**
   * The stored failure classification projected verbatim from the row
   * (`failure_kind` on `tasks`), or null when the row carries no kind this
   * build knows (#2574). The detail drawer renders `failure_reason` when this
   * is non-null — both arrive together, never one without the other.
   */
  readonly failure_kind: TaskFailureKind | null;
  /**
   * ONE human sentence describing what stopped this task (#2574). null when
   * the row carries no failure — never an empty string. The sentence reads as
   * a refusal before any box claimed the task (attempt 0) and as a
   * ladder-exhausted failure (3 attempts spent) — same `failure_kind`,
   * different sentences, exactly the discriminator the issue names.
   */
  readonly failure_reason: string | null;
  readonly run: TaskRunPulse | null;
};

export const TASK_DETAIL_QUERY_KEY = 'fleet_task_detail';

/**
 * The backstop re-read for both reads below, kept on while the stream is live.
 * What the stream cannot tell them: the run pulse ({@link TaskRunPulse}) is
 * derived from run events, which never bump `tasks.updated_at`, and the writers
 * that change a task without that bump (`live/entities.ts` names them).
 */
export const TASK_REFRESH_MS = 60_000;

/** Both reads below hold ONE task: the `tasks` / `task_changes` topics moving it is a change to them. */
const heldTask = (taskId: string | null): readonly LiveInterest[] =>
  taskId === null ? [] : [{ entity: 'task', id: taskId }];

/**
 * One task in full. Idle until a row is selected — the drawer gates on its id.
 * An unknown id AND another account's task both 404 (the API never confirms a
 * foreign task exists); the drawer reads that 404 as "gone", not as a failure.
 *
 * LIVE (`lib/live/live-query.ts`): a patch for this id on `tasks` or
 * `task_changes` — any write that bumps the task's `updated_at`, its status
 * moves among them — re-reads it at once, so an open drawer or a run header
 * follows the task's STATUS instead of holding the one it opened with. The run
 * pulse and the no-bump writers reach it within {@link TASK_REFRESH_MS}.
 */
export function useTaskDetail(taskId: () => string | null, token: string | null = SESSION_TOKEN) {
  return createLiveQuery<TaskDetail>(
    () => ({
      queryKey: [TASK_DETAIL_QUERY_KEY, taskId(), token, activeAccountId()],
      enabled: token !== null && taskId() !== null,
      queryFn: () => apiFetch<TaskDetail>(`/v1/tasks/${encodeURIComponent(taskId() ?? '')}`, token),
      live: heldTask(taskId()),
      refreshMs: TASK_REFRESH_MS,
    }),
    token,
  ).query;
}

export const TASK_MOVE_READ_KEY = 'fleet_task_move_read';

/**
 * A task's newest move, stamped with when its request STARTED in the page's order (#6772,
 * `./read-order.ts`): the Fleet page judges a move it accepted only by reads that started after
 * the 201 (`./move-read.ts`). Its own key, not the detail's: a detail cached by any other view
 * carries no start stamp. Live on the task like {@link useTaskDetail}; `refresh` re-reads it now.
 */
export function useTaskMoveRead(
  taskId: () => string,
  token: string | null,
): { readonly data: MoveRead | undefined; readonly refresh: () => void } {
  const client = useQueryClient();
  const key = (): QueryKey => [TASK_MOVE_READ_KEY, taskId(), token, activeAccountId()];
  const query = createLiveQuery<MoveRead>(
    () => ({
      queryKey: key(),
      enabled: token !== null,
      queryFn: async () => {
        const order = orderStamp();
        const detail = await apiFetch<TaskDetail>(
          `/v1/tasks/${encodeURIComponent(taskId())}`,
          token,
        );
        // Held: still a job a machine holds; Put back lets a task go once it is not (`heldBy`).
        const held = heldBy(detail.status);
        return { move: detail.move, order, held, status: detail.status };
      },
      live: heldTask(taskId()),
      refreshMs: TASK_REFRESH_MS,
    }),
    token,
  ).query;
  return {
    get data() {
      return query.data;
    },
    refresh: () => void client.invalidateQueries({ queryKey: key(), exact: true }),
  };
}

export const TASK_ATTEMPTS_QUERY_KEY = 'fleet_task_attempts';

/**
 * The per-task attempt ladder (#2760) — one row per attempt the task has
 * accumulated, ordered by the seq of each attempt's first event so attempt 1
 * reads first. Joined by `task_id`, NEVER `run_id` (which `tasks.run_id`
 * holds and overwrites on retry — the join-by-run-id bug the ladder exists
 * to fix).
 *
 * Fetches `/v1/tasks/:id/attempts` — one call per task, no cursor, no
 * pagination. The ladder is bounded by retry count in practice (a task
 * burns one ladder per kind, bounded by `TASK_MAX_ATTEMPTS`); the API mints
 * no cursor.
 *
 * Idle until a task id is selected, like {@link useTaskDetail} above. A
 * 404 reads as "the task is gone" — the drawer already handles that for
 * `useTaskDetail`, so the ladder is hidden when the row it lives inside
 * is missing.
 */
export function useTaskAttempts(taskId: () => string | null, token: string | null = SESSION_TOKEN) {
  return createLiveQuery<readonly TaskAttempt[]>(
    () => ({
      queryKey: [TASK_ATTEMPTS_QUERY_KEY, taskId(), token, activeAccountId()],
      enabled: token !== null && taskId() !== null,
      queryFn: () =>
        apiFetch<readonly TaskAttempt[]>(
          `/v1/tasks/${encodeURIComponent(taskId() ?? '')}/attempts`,
          token,
        ),
      live: heldTask(taskId()),
      refreshMs: TASK_REFRESH_MS,
    }),
    token,
  ).query;
}
